import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TicketCard } from '../../components/custom/TicketCard';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Ticket, Clock, CheckCircle, AlertTriangle } from 'lucide-react';

export default function ClientDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, resolved: 0, actionRequired: 0 });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/tickets');
      const ticketData = response.data.tickets;
      setTickets(ticketData);
      
      // Calculate stats
      setStats({
        total: ticketData.length,
        pending: ticketData.filter(t => ['pending', 'open'].includes(t.status)).length,
        resolved: ticketData.filter(t => ['resolved', 'closed'].includes(t.status)).length,
        actionRequired: ticketData.filter(t => t.client_action_required).length
      });
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeTickets = tickets.filter(t => !['resolved', 'closed'].includes(t.status));
  const resolvedTickets = tickets.filter(t => ['resolved', 'closed'].includes(t.status));

  return (
    <div data-testid="client-dashboard" className="space-y-8">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Bonjour, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-slate-600 dark:text-zinc-400 mt-1">
            Gérez vos demandes de support
          </p>
        </div>
        <Button
          onClick={() => navigate('/portal/new')}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau ticket
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {loading ? <Skeleton className="h-7 w-8" /> : stats.total}
                </p>
                <p className="text-xs text-slate-500 dark:text-zinc-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {loading ? <Skeleton className="h-7 w-8" /> : stats.pending}
                </p>
                <p className="text-xs text-slate-500 dark:text-zinc-400">En cours</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {loading ? <Skeleton className="h-7 w-8" /> : stats.resolved}
                </p>
                <p className="text-xs text-slate-500 dark:text-zinc-400">Résolus</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {loading ? <Skeleton className="h-7 w-8" /> : stats.actionRequired}
                </p>
                <p className="text-xs text-slate-500 dark:text-zinc-400">Action requise</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Tickets */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
          Tickets actifs
        </h2>
        
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Card key={i} className="border-slate-200 dark:border-zinc-800">
                <CardContent className="p-5">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : activeTickets.length === 0 ? (
          <Card className="border-slate-200 dark:border-zinc-800">
            <CardContent className="p-12 text-center">
              <Ticket className="w-12 h-12 text-slate-300 dark:text-zinc-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                Aucun ticket actif
              </h3>
              <p className="text-slate-600 dark:text-zinc-400 mb-4">
                Vous n'avez pas de demande en cours.
              </p>
              <Button
                onClick={() => navigate('/portal/new')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {activeTickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} isStaff={false} />
            ))}
          </div>
        )}
      </div>

      {/* Resolved Tickets */}
      {resolvedTickets.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
            Historique
          </h2>
          <div className="space-y-4">
            {resolvedTickets.slice(0, 5).map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} isStaff={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
